<template>
  <v-dialog
    v-model="isVisible"
    max-width="565"
    @click:outside="$emit('onClose')"
    @keydown.esc="$emit('onClose')"
  >
    <v-card style="background-color: #1a1a1a" class="ma-0" outlined>
      <v-form ref="modalForm" @submit.prevent="submitEquipmentChanges">
        <v-row class="mr-6 ml-6 mb-0 pb-0 flex-nowrap">
          <h1 class="pa-0 mt-6">{{ modalTitle }}</h1>
          <v-spacer />
          <v-icon @click="$emit('onClose')" class="mt-7 mr-1 flex-shrink-0">
            $iconClose
          </v-icon>
        </v-row>
        <v-row class="mr-6 ml-6 mt-6" justify="center">
          <v-col class="ml-0 mr-0 pa-0" max-width="514">
            <div v-for="(equipment, index) in localEquipments" :key="index">
              <v-subheader class="mb-1 ml-0 pl-0" style="height: auto"
                >{{ $t("project.modal.input.equipment") }}
              </v-subheader>
              <v-text-field
                v-model.trim="equipment.equipment_type"
                max-width="514px"
                class="mb-4"
                rounded
                outlined
                filled
                hide-details="auto"
                :rules="[
                  (v) => !!v || $t('project.modal.test.equipment.error'),
                ]"
              />
              <v-subheader class="mb-1 ml-0 pl-0" style="height: auto"
                >{{ $t("project.modal.input.calibrated") }}
              </v-subheader>
              <v-text-field
                v-model.trim="equipment.date_calibrated"
                max-width="514px"
                class="mb-4"
                rounded
                outlined
                filled
                :hint="$t('project.modal.input.hint')"
                hide-details="auto"
                :rules="[
                  (v) => !!v || $t('project.modal.test.calibrated.error'),
                ]"
                persistent-hint
              />
              <v-row
                v-if="localEquipments.length > 1"
                class="ml-0 mr-2 mb-2 pb-2 flex-nowrap"
              >
                <v-spacer></v-spacer>
                <v-icon @click="removeEquipment(index)">$iconDelete</v-icon>
              </v-row>
              <v-divider
                v-if="localEquipments.length > 1"
                color="#4C4C4C"
                class="mb-6"
              ></v-divider>
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="localEquipments.length < maxEquipments"
          class="row mr-6 ml-6 pb-5 mt-3 justify-end"
        >
          <niko-text-button
            :text="$t('project.modal.button.add_additional_equipment')"
            color="primary"
            class="ma-0"
            icon-before="$iconAdd"
            :alignLeft="true"
            @onClick="addEquipment"
          ></niko-text-button>
        </v-row>
        <v-row class="mr-6 ml-6 pb-5 mt-3 justify-end">
          <v-card-actions class="ma-0 pa-0">
            <niko-button
              plain
              :text="modalSecondaryButton"
              @onClick="$emit('onClose')"
            ></niko-button>
            <niko-button
              class="ml-4"
              :text="modalPrimaryButton"
              type="submit"
              primary
            ></niko-button>
          </v-card-actions>
        </v-row>
      </v-form>
    </v-card>
  </v-dialog>
</template>
<script>
import NikoButton from "../UI/NikoButton";
import { mapActions, mapState } from "vuex";
import NikoTextButton from "@/components/UI/NikoTextButton.vue";

export default {
  components: { NikoTextButton, NikoButton },
  name: "testing-equipment-modal",
  props: {
    isVisible: {
      type: Boolean,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => {
        return ["edit", "add"].includes(value);
      },
    },
  },
  data() {
    return {
      localEquipments: [],
      maxEquipments: 3,
    };
  },
  computed: {
    ...mapState("projectStore", ["name", "equipments"]),
    modalTitle() {
      return this.mode === "edit"
        ? this.$i18n.t("project.modal.title.equipment")
        : this.$i18n.t("project.modal.test.title", { project: this.name });
    },
    modalPrimaryButton() {
      return this.mode === "edit"
        ? this.$i18n.t("project.modal.button.save")
        : this.$i18n.t("project.modal.test.start");
    },
    modalSecondaryButton() {
      return this.mode === "edit"
        ? this.$i18n.t("project.modal.button.cancel")
        : this.$i18n.t("project.modal.test.later");
    },
  },
  methods: {
    ...mapActions("projectStore", ["updateEquipment"]),
    addEquipment() {
      if (this.localEquipments.length < this.maxEquipments) {
        this.localEquipments.push({ equipment_type: "", date_calibrated: "" });
      }
    },
    removeEquipment(index) {
      this.localEquipments.splice(index, 1);
      this.$refs.modalForm.resetValidation();
    },
    async submitEquipmentChanges() {
      if (this.$refs.modalForm.validate()) {
        await this.updateEquipment(
          this.localEquipments.map((equipment) => ({
            equipment_type: equipment.equipment_type,
            date_calibrated: equipment.date_calibrated,
          }))
        );
        this.$emit("onSubmit");
      }
    },
    initializeModal() {
      this.localEquipments = this.equipments.slice();
      if (this.localEquipments.length === 0) {
        this.addEquipment();
      }
    },
  },
  mounted() {
    if (this.isVisible) {
      this.initializeModal();
    }
  },
  watch: {
    isVisible(value) {
      if (value) {
        this.initializeModal();
      }
    },
  },
};
</script>
<style scoped>
.v-text-field--filled:not(.v-text-field--single-line) input {
  margin-top: 1px;
}
.v-subheader {
  color: white;
}
.v-input__control {
  align-items: center;
}
.v-card:not(.v-sheet--tile):not(.v-card--shaped) {
  border-radius: 16px;
}
.theme--dark.v-card.v-card--outlined {
  border: thin solid rgba(255, 255, 255, 0.5);
}
</style>
